import { faChevronLeft, faChevronRight, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Form, Row, Table } from "react-bootstrap";
import ConditionalSpinner from "../../components/ConditionalSpinner";
import {
    useGetAllTagEventsQuery,
    useGetAllTagObjectsQuery,
    useGetAuditLogEntriesQuery,
    useGetProjectsQuery,
} from "../../store/api/kinesense";
import { capitaliseFirst } from "../../utilities/helpers";
import "./AuditLog.scss";
import { ApplicationState } from "../../store";
import { useSelector } from "react-redux";
import { addAuthHeaders } from "../../utilities/Auth";
import { TagEvent } from "../../models/tags/TagEvent";

type SortField = "addedAt" | "entryType" | "origin" | "sessionId";
type SortDirection = "asc" | "desc";

interface SortConfig {
    field: SortField;
    direction: SortDirection;
}

export function AuditLog() {
    // State for filtering and pagination
    const [primaryCategory, setPrimaryCategory] = useState<string>("");
    const [secondaryCategory, setSecondaryCategory] = useState<string>("");
    const [tertiaryCategory, setTertiaryCategory] = useState<string>("");
    const [limit, setLimit] = useState<number>(50);
    const [pageKey, setPageKey] = useState<string | undefined>(undefined);
    const [pageHistory, setPageHistory] = useState<string[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ field: "addedAt", direction: "desc" });
    const [isDownloading, setIsDownloading] = useState(false);
    const { data: projects, isSuccess } = useGetProjectsQuery();
    const user = useSelector((state: ApplicationState) => state.user);

    const possibleEntryTypes = [
        "tag:event:created",
        "tag:event:deleted",
        "tag:object:created",
        "tag:object:deleted",
        "project:new",
        "permission:new",
        "permission:modified",
        "user:new",
        "org:new",
        "processor:new",
    ];

    // Extract primary categories (top-level)
    const primaryCategories = useMemo(() => {
        const categories = new Set<string>();
        possibleEntryTypes.forEach((type) => {
            const firstPart = type.split(":")[0];
            categories.add(firstPart);
        });
        return Array.from(categories).sort();
    }, []);

    // Get secondary categories based on selected primary category
    const secondaryCategories = useMemo(() => {
        if (!primaryCategory) return [];

        const subcategories = new Set<string>();
        possibleEntryTypes.forEach((type) => {
            if (type.startsWith(primaryCategory + ":")) {
                const parts = type.split(":");
                if (parts.length >= 2) {
                    subcategories.add(parts[1]);
                }
            }
        });
        return Array.from(subcategories).sort();
    }, [primaryCategory]);

    // Get tertiary categories based on selected primary and secondary categories
    const tertiaryCategories = useMemo(() => {
        if (!primaryCategory || !secondaryCategory) return [];

        const subsubcategories = new Set<string>();
        possibleEntryTypes.forEach((type) => {
            const prefix = `${primaryCategory}:${secondaryCategory}:`;
            if (type.startsWith(prefix)) {
                const parts = type.split(":");
                if (parts.length >= 3) {
                    subsubcategories.add(parts[2]);
                }
            }
        });
        return Array.from(subsubcategories).sort();
    }, [primaryCategory, secondaryCategory]);

    // Build the final type filter for API query
    const typeFilter = useMemo(() => {
        if (!primaryCategory) return "";
        if (!secondaryCategory) return primaryCategory;
        if (!tertiaryCategory) return `${primaryCategory}:${secondaryCategory}`;
        return `${primaryCategory}:${secondaryCategory}:${tertiaryCategory}`;
    }, [primaryCategory, secondaryCategory, tertiaryCategory]);

    const hasSecondary = primaryCategory && secondaryCategories.length > 0;
    const hasTertiary = primaryCategory && secondaryCategory && tertiaryCategories.length > 0;

    // API query
    const queryParams = {
        typePrefix: typeFilter || undefined,
        pageKey: pageKey || undefined,
        limit,
    };

    const { data: auditLogEntriesQuery, error, isLoading, isFetching } = useGetAuditLogEntriesQuery(queryParams);
    const auditLogEntries = auditLogEntriesQuery?.data || [];
    const hasNextPage = auditLogEntriesQuery?.hasNextPage || false;

    // Extract unique project IDs from tag events in current audit log entries
    const projectIdsWithTagEvents = useMemo(() => {
        const projectIds = new Set<string>();
        auditLogEntries.forEach((entry) => {
            if (entry.entryType.startsWith("tag:event") && entry.data && typeof entry.data === "object") {
                const data = entry.data as { projectId?: string };
                if (data.projectId) {
                    projectIds.add(data.projectId);
                }
            }
        });
        return Array.from(projectIds);
    }, [auditLogEntries]);

    // Get the first project ID (we'll expand this if we need to support multiple projects)
    const firstProjectId = projectIdsWithTagEvents[0];
    const secondProjectId = projectIdsWithTagEvents[1];
    const thirdProjectId = projectIdsWithTagEvents[2];

    // Fetch tag events for up to 3 projects that have tag events in the current audit log
    // This covers most common scenarios while respecting the rules of hooks
    const { data: tagEvents1 } = useGetAllTagEventsQuery({ projectId: firstProjectId }, { skip: !firstProjectId });
    const { data: tagEvents2 } = useGetAllTagEventsQuery({ projectId: secondProjectId }, { skip: !secondProjectId });
    const { data: tagEvents3 } = useGetAllTagEventsQuery({ projectId: thirdProjectId }, { skip: !thirdProjectId });

    // Create a map of tag event ID to tag event data for quick lookup
    const tagEventsMap = useMemo(() => {
        const map = new Map<string, TagEvent>();

        // Combine tag events from all projects
        [tagEvents1, tagEvents2, tagEvents3].forEach((tagEvents) => {
            if (tagEvents) {
                tagEvents.forEach((tagEvent) => {
                    map.set(tagEvent.eventId, tagEvent);
                });
            }
        });

        return map;
    }, [tagEvents1, tagEvents2, tagEvents3]);

    // Extract unique project IDs from tag objects in current audit log entries
    const projectIdsWithTagObjects = useMemo(() => {
        const projectIds = new Set<string>();
        auditLogEntries.forEach((entry) => {
            if (entry.entryType.startsWith("tag:object") && entry.data && typeof entry.data === "object") {
                const data = entry.data as { projectId?: string };
                if (data.projectId) {
                    projectIds.add(data.projectId);
                }
            }
        });
        return Array.from(projectIds);
    }, [auditLogEntries]);

    // Get the first few project IDs for tag objects
    const firstTagObjectProjectId = projectIdsWithTagObjects[0];
    const secondTagObjectProjectId = projectIdsWithTagObjects[1];
    const thirdTagObjectProjectId = projectIdsWithTagObjects[2];

    // Fetch tag objects for up to 3 projects that have tag objects in the current audit log
    const { data: tagObjects1 } = useGetAllTagObjectsQuery(
        { projectId: firstTagObjectProjectId },
        { skip: !firstTagObjectProjectId },
    );
    const { data: tagObjects2 } = useGetAllTagObjectsQuery(
        { projectId: secondTagObjectProjectId },
        { skip: !secondTagObjectProjectId },
    );
    const { data: tagObjects3 } = useGetAllTagObjectsQuery(
        { projectId: thirdTagObjectProjectId },
        { skip: !thirdTagObjectProjectId },
    );

    // Create a map of tag object ID to tag object data for quick lookup
    const tagObjectsMap = useMemo(() => {
        const map = new Map<string, { name: string; projectId: string }>();

        // Combine tag objects from all projects
        [tagObjects1, tagObjects2, tagObjects3].forEach((tagObjects) => {
            if (tagObjects) {
                tagObjects.forEach((tagObject) => {
                    map.set(tagObject.objectId, {
                        name: tagObject.name,
                        projectId: tagObject.projectId,
                    });
                });
            }
        });

        return map;
    }, [tagObjects1, tagObjects2, tagObjects3]);

    // Update page history when navigation occurs
    useEffect(() => {
        // Skip initial mount when no pageKey is set
        if (pageKey === undefined) {
            return;
        }

        // When pageKey is empty string, reset to first page
        if (pageKey === "") {
            if (pageHistory.length > 0 || currentPageIndex > 0) {
                setPageHistory([]);
                setCurrentPageIndex(0);
            }
            return;
        }

        // Check if this pageKey is already in our history
        const existingIndex = pageHistory.findIndex((key) => key === pageKey);

        if (existingIndex !== -1) {
            // We're navigating to a page that's already in history
            // pageHistory[0] corresponds to page 2, pageHistory[1] to page 3, etc.
            setCurrentPageIndex(existingIndex + 1);
        } else {
            // This is a new page, add it to history
            // Only add if we're currently at the end of our history (going forward)
            if (currentPageIndex === pageHistory.length) {
                setPageHistory((prev) => [...prev, pageKey]);
                setCurrentPageIndex((prev) => prev + 1);
            }
        }
    }, [pageKey]); // Only depend on pageKey changes

    const formatOrigin = (origin: { type: string; id: string }) => {
        if (!origin) return "Unknown";

        if (origin.type === "user") {
            return origin.id;
        } else if (origin.type === "system") {
            return "System";
        }

        return "Unknown";
    };

    // Sort entries
    const sortedEntries = useMemo(() => {
        if (!auditLogEntries) return [];

        const sorted = [...auditLogEntries].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            switch (sortConfig.field) {
                case "addedAt":
                    aValue = a.addedAt;
                    bValue = b.addedAt;
                    break;
                case "entryType":
                    aValue = a.entryType;
                    bValue = b.entryType;
                    break;
                case "origin":
                    aValue = formatOrigin(a.origin);
                    bValue = formatOrigin(b.origin);
                    break;
                case "sessionId":
                    aValue = a.sessionId;
                    bValue = b.sessionId;
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === "string" && typeof bValue === "string") {
                const comparison = aValue.localeCompare(bValue);
                return sortConfig.direction === "asc" ? comparison : -comparison;
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [auditLogEntries, sortConfig]);

    const handleSort = (field: SortField) => {
        setSortConfig((current) => ({
            field,
            direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (field: SortField) => {
        if (sortConfig.field !== field) return faSort;
        return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    };

    // Helper function to reset pagination state
    const resetPagination = () => {
        setPageKey(undefined);
        setPageHistory([]);
        setCurrentPageIndex(0);
    };

    const handlePrimaryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPrimaryCategory(e.target.value);
        setSecondaryCategory(""); // Reset secondary category when primary changes
        setTertiaryCategory(""); // Reset tertiary category when primary changes
        resetPagination();
    };

    const handleSecondaryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSecondaryCategory(e.target.value);
        setTertiaryCategory(""); // Reset tertiary category when secondary changes
        resetPagination();
    };

    const handleTertiaryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTertiaryCategory(e.target.value);
        resetPagination();
    };

    const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLimit(parseInt(e.target.value));
        resetPagination();
    };

    // Pagination navigation handlers
    const goToFirstPage = () => {
        setPageKey(""); // Use empty string to trigger reset
    };

    const goToPreviousPage = () => {
        if (currentPageIndex > 1) {
            const previousPageKey = pageHistory[currentPageIndex - 2]; // -2 because history is 0-indexed and we want the previous
            setPageKey(previousPageKey);
            // Let useEffect handle updating currentPageIndex
        } else if (currentPageIndex === 1) {
            goToFirstPage();
        }
    };

    const goToNextPage = () => {
        if (hasNextPage && auditLogEntriesQuery?.pageKey) {
            setPageKey(auditLogEntriesQuery.pageKey);
            // Let useEffect handle updating currentPageIndex
        }
    };

    const formatTimestamp = (timestamp: number) => {
        return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
    };

    const formatCategory = (category: string) => {
        switch (category) {
            case "org":
                return "Organisation";
            default:
                return capitaliseFirst(category);
        }
    };

    const formatData = (data: unknown) => {
        if (!data) return "-";
        if (typeof data === "string") return data;
        if (typeof data === "object") {
            try {
                return JSON.stringify(data, null, 2);
            } catch {
                return String(data);
            }
        }
        return String(data);
    };

    const getProjectName = (projectId: string) => {
        if (!projects || !isSuccess) return projectId;
        const project = projects.find((p) => p.projectId === projectId);
        return project ? project.name : projectId;
    };

    const renderTagEventData = (data: { tagEventId: string; projectId: string }) => {
        const tagEvent = tagEventsMap.get(data.tagEventId);

        return (
            <td>
                <div>
                    <div>
                        <small className="text-muted">
                            {tagEvent ? (
                                <span>{TagEvent.createSummary(tagEvent)}</span>
                            ) : (
                                <span className="text-muted">Loading event details...</span>
                            )}
                        </small>
                    </div>
                    <div>
                        <small className="text-muted">
                            <em>Project: {getProjectName(data.projectId)}</em>
                        </small>
                    </div>
                </div>
            </td>
        );
    };

    const renderTagObjectData = (data: { tagObjectId: string; projectId: string }) => {
        const tagObject = tagObjectsMap.get(data.tagObjectId);

        return (
            <td>
                <div>
                    <div>
                        <small className="text-muted">
                            {tagObject ? (
                                <span>{tagObject.name}</span>
                            ) : (
                                <span className="text-muted">Loading object name...</span>
                            )}
                        </small>
                    </div>
                    <div>
                        <small className="text-muted">
                            <em>Project: {getProjectName(data.projectId)}</em>
                        </small>
                    </div>
                </div>
            </td>
        );
    };

    const renderData = (type: string, data: unknown) => {
        if (type.startsWith("tag:event") && data) {
            return renderTagEventData(data as { tagEventId: string; projectId: string });
        }

        if (type.startsWith("tag:object") && data) {
            return renderTagObjectData(data as { tagObjectId: string; projectId: string });
        }

        return (
            <td>
                <details>
                    <summary style={{ cursor: "pointer" }}>View Data</summary>
                    <pre
                        className="mt-2 p-2 bg-light border rounded"
                        style={{ fontSize: "0.8rem", maxHeight: "200px", overflow: "auto" }}
                    >
                        {formatData(data)}
                    </pre>
                </details>
            </td>
        );
    };

    function downloadAuditLog(): void {
        if (!user) {
            console.error("User not authenticated");
            return;
        }

        setIsDownloading(true);

        // Use the current filter to download the CSV
        const typePrefix = typeFilter || undefined;
        const params = new URLSearchParams();
        if (typePrefix) {
            params.append("type", typePrefix);
        }

        const url = `https://${process.env.REACT_APP_KINESENSE_API_BASE}/audit/entries/export${
            params.toString() ? `?${params.toString()}` : ""
        }`;

        const headers = new Headers();
        addAuthHeaders(user.authToken, headers);

        fetch(url, {
            method: "GET",
            headers,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => {
                // Create a download link and trigger download
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;

                // Generate filename based on filter and current date
                const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
                const filterSuffix = typePrefix ? `_${typePrefix.replaceAll(":", "-")}` : "";
                link.download = `audit-log${filterSuffix}_${timestamp}.csv`;

                // Append to body, click, and remove
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the blob URL
                window.URL.revokeObjectURL(downloadUrl);
            })
            .catch((error) => {
                console.error("Failed to download audit log:", error);
                // You might want to show a user-friendly error message here
            })
            .finally(() => {
                setIsDownloading(false);
            });
    }

    return (
        <main className="container audit-log-container">
            <Row>
                <Col>
                    <h2 className="mb-4">Audit Log</h2>

                    {/* Filtering & Navigation */}
                    <div className="table-controls">
                        <div className="filters">
                            <span className="filter-label">Category</span>
                            <span className="filter-label">Subcategory</span>
                            <span className="filter-label">Action</span>
                            <Form.Select
                                id="primary-category-filter"
                                value={primaryCategory}
                                onChange={handlePrimaryCategoryChange}
                                disabled={isLoading}
                            >
                                <option value="">All categories</option>
                                {primaryCategories.map((category) => (
                                    <option key={category} value={category}>
                                        {formatCategory(category)}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                id="secondary-category-filter"
                                value={secondaryCategory}
                                onChange={handleSecondaryCategoryChange}
                                disabled={isLoading || !hasSecondary}
                            >
                                <option value="">All {primaryCategory} types</option>
                                {secondaryCategories.map((subcategory) => (
                                    <option key={subcategory} value={subcategory}>
                                        {formatCategory(subcategory)}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                id="tertiary-category-filter"
                                value={tertiaryCategory}
                                onChange={handleTertiaryCategoryChange}
                                disabled={isLoading || !hasTertiary}
                            >
                                <option value="">All {secondaryCategory} actions</option>
                                {tertiaryCategories.map((action) => (
                                    <option key={action} value={action}>
                                        {formatCategory(action)}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        <div></div>

                        <div className="item-stats">
                            <div className="filter-label">Items per page</div>
                            <Form.Select
                                id="limit-select"
                                value={limit}
                                onChange={handleLimitChange}
                                disabled={isLoading}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </Form.Select>
                        </div>

                        <div className="navigation">
                            <div className="filter-label">&nbsp;</div>
                            <ButtonGroup>
                                <Button
                                    variant="outline-secondary"
                                    onClick={goToFirstPage}
                                    disabled={isLoading || currentPageIndex === 0}
                                    title="Go to first page"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} /> First
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={goToPreviousPage}
                                    disabled={isLoading || currentPageIndex === 0}
                                    title="Go to previous page"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                                </Button>
                                <Button variant="outline-secondary" disabled={true}>
                                    Page {currentPageIndex + 1}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={goToNextPage}
                                    disabled={isLoading || !hasNextPage}
                                    title="Go to next page"
                                >
                                    Next <FontAwesomeIcon icon={faChevronRight} />
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    {/* Loading/Error States */}
                    {error && <Alert variant="danger">Error loading audit log entries. Please try again.</Alert>}

                    {/* Table */}
                    <ConditionalSpinner isLoading={isLoading}>
                        <Table striped bordered hover responsive className="audit-log-table">
                            <thead>
                                <tr>
                                    <th
                                        style={{ cursor: "pointer", userSelect: "none" }}
                                        onClick={() => handleSort("addedAt")}
                                    >
                                        Date & Time <FontAwesomeIcon icon={getSortIcon("addedAt")} />
                                    </th>
                                    <th
                                        style={{ cursor: "pointer", userSelect: "none" }}
                                        onClick={() => handleSort("entryType")}
                                    >
                                        Entry Type <FontAwesomeIcon icon={getSortIcon("entryType")} />
                                    </th>
                                    <th
                                        style={{ cursor: "pointer", userSelect: "none" }}
                                        onClick={() => handleSort("origin")}
                                    >
                                        User <FontAwesomeIcon icon={getSortIcon("origin")} />
                                    </th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedEntries.length === 0 && !isLoading && (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted">
                                            No audit log entries found
                                        </td>
                                    </tr>
                                )}
                                {sortedEntries.map((entry, index) => (
                                    <tr key={`${entry.sessionId}-${entry.addedAt}-${index}`}>
                                        <td>{formatTimestamp(entry.addedAt)}</td>
                                        <td>
                                            <code>{entry.entryType.replaceAll(":", " ")}</code>
                                        </td>
                                        <td>{formatOrigin(entry.origin)}</td>
                                        {renderData(entry.entryType, entry.data)}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ConditionalSpinner>

                    {/* Pagination Info */}
                    {sortedEntries.length > 0 && (
                        <Row className="mt-3">
                            <Col>
                                <small className="text-muted">
                                    Showing {sortedEntries.length} entries
                                    {typeFilter && ` filtered by "${typeFilter.replaceAll(":", " > ")}"`}
                                    {currentPageIndex > 0 && ` • Page ${currentPageIndex + 1}`}
                                    {hasNextPage && " • More pages available"}
                                    {isFetching && " (refreshing...)"}
                                </small>
                            </Col>
                            <Col className="text-end">
                                <Button onClick={downloadAuditLog} disabled={isDownloading} variant="outline-primary">
                                    {isDownloading ? "Downloading..." : "Download CSV"}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
        </main>
    );
}
